import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, Save } from 'lucide-react';
import { useSystem } from '@ohif/core';
import { ICON_STROKE_WIDTH } from '../../constants';
import { CRITICALITY_BANDS } from '../../constants/escalationMatrix.constants';
import { listEscalationContacts } from '../../services/escalationContactService';
import {
  activateEscalationMatrix,
  createEscalationMatrix,
  getEscalationMatrixByBand,
  listEscalationMatrices,
  updateEscalationMatrix,
} from '../../services/escalationMatrixService';
import type { EscalationContact } from '../../types/escalationContact.types';
import type {
  CriticalityBand,
  EscalationMatrix,
  MatrixChainFormState,
  MatrixValidationError,
} from '../../types/escalationMatrix.types';
import {
  buildDefaultChainForm,
  buildMatrixPayload,
  mapMatrixToChainForm,
} from '../../utils/escalationMatrixFormUtils';
import {
  groupFieldErrorsByLevel,
  validateForActivation,
  validateMatrixChain,
} from '../../utils/escalationMatrixValidation';
import EscalationChainCard from './EscalationChainCard';
import EscalationValidationSummary from './EscalationValidationSummary';

interface EscalationMatrixSectionProps {
  facilityId: number;
}

export interface EscalationMatrixSectionHandle {
  refreshActiveContacts: () => Promise<void>;
}

function sanitizeChainsForActiveContacts(
  chains: Record<CriticalityBand, MatrixChainFormState>,
  activeContacts: EscalationContact[]
): Record<CriticalityBand, MatrixChainFormState> {
  const activeIds = new Set(activeContacts.map(contact => contact.id));
  const sanitize = (chain: MatrixChainFormState): MatrixChainFormState => ({
    ...chain,
    levels: chain.levels.map(level => ({
      ...level,
      contactId: level.contactId != null && activeIds.has(level.contactId) ? level.contactId : null,
    })),
  });

  return {
    Major: sanitize(chains.Major),
    Minor: sanitize(chains.Minor),
  };
}

function resolveChainFromMatrices(
  band: CriticalityBand,
  draft: EscalationMatrix | undefined,
  active: EscalationMatrix | undefined
): MatrixChainFormState {
  const source = draft || active;
  if (!source) {
    return buildDefaultChainForm(band);
  }
  const chain = mapMatrixToChainForm(source);
  return {
    ...chain,
    status: source.status,
    activeMatrixId: active?.id,
  };
}

export default forwardRef<EscalationMatrixSectionHandle, EscalationMatrixSectionProps>(
  function EscalationMatrixSection({ facilityId }, ref) {
    const { servicesManager } = useSystem();
    const { uiNotificationService } = servicesManager.services;

    const [chains, setChains] = useState<Record<CriticalityBand, MatrixChainFormState>>({
      Major: buildDefaultChainForm('Major'),
      Minor: buildDefaultChainForm('Minor'),
    });
    const [contacts, setContacts] = useState<EscalationContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const [activationErrors, setActivationErrors] = useState<MatrixValidationError[]>([]);
    const [validationErrors, setValidationErrors] = useState<
      Record<CriticalityBand, MatrixValidationError[]>
    >({
      Major: [],
      Minor: [],
    });
    const [fieldErrorsByBand, setFieldErrorsByBand] = useState<
      Record<CriticalityBand, ReturnType<typeof groupFieldErrorsByLevel>>
    >({ Major: {}, Minor: {} });

    const loadData = useCallback(async () => {
      setLoading(true);
      setLoadError(null);

      const [contactsRes, listRes] = await Promise.all([
        listEscalationContacts({ facilityId, status: 'Active' }),
        listEscalationMatrices(facilityId),
      ]);

      if (!contactsRes.success) {
        setLoadError(contactsRes.error || 'Failed to load active contacts');
      }

      const drafts = new Map<CriticalityBand, EscalationMatrix>();
      const actives = new Map<CriticalityBand, EscalationMatrix>();
      (listRes.data || []).forEach(matrix => {
        if (matrix.status === 'Draft') {
          drafts.set(matrix.criticalityBand, matrix);
        }
        if (matrix.status === 'Active') {
          actives.set(matrix.criticalityBand, matrix);
        }
      });

      setContacts(contactsRes.data || []);
      setChains({
        Major: resolveChainFromMatrices('Major', drafts.get('Major'), actives.get('Major')),
        Minor: resolveChainFromMatrices('Minor', drafts.get('Minor'), actives.get('Minor')),
      });
      setLoading(false);
    }, [facilityId]);

    useEffect(() => {
      loadData();
    }, [loadData]);

    const refreshActiveContacts = useCallback(async () => {
      const contactsRes = await listEscalationContacts({ facilityId, status: 'Active' });
      if (!contactsRes.success) {
        return;
      }

      const activeContacts = contactsRes.data || [];
      setContacts(activeContacts);
      setChains(prev => {
        const next = sanitizeChainsForActiveContacts(prev, activeContacts);
        if (showValidation) {
          const majorErrors = validateMatrixChain(next.Major, activeContacts);
          const minorErrors = validateMatrixChain(next.Minor, activeContacts);
          setValidationErrors({ Major: majorErrors, Minor: minorErrors });
          setFieldErrorsByBand({
            Major: groupFieldErrorsByLevel(majorErrors),
            Minor: groupFieldErrorsByLevel(minorErrors),
          });
          setActivationErrors(validateForActivation(next, activeContacts));
        }
        return next;
      });
    }, [facilityId, showValidation]);

    useImperativeHandle(ref, () => ({ refreshActiveContacts }), [refreshActiveContacts]);

    const handleChainChange = (band: CriticalityBand, chain: MatrixChainFormState) => {
      setChains(prev => {
        const next = {
          ...prev,
          [band]: { ...chain, status: 'Draft' },
        };
        if (showValidation) {
          const majorErrors = validateMatrixChain(next.Major, contacts);
          const minorErrors = validateMatrixChain(next.Minor, contacts);
          setValidationErrors({ Major: majorErrors, Minor: minorErrors });
          setFieldErrorsByBand({
            Major: groupFieldErrorsByLevel(majorErrors),
            Minor: groupFieldErrorsByLevel(minorErrors),
          });
          setActivationErrors(validateForActivation(next, contacts));
        }
        return next;
      });
    };

    const persistChain = async (chain: MatrixChainFormState) => {
      const payload = buildMatrixPayload(chain, facilityId);
      if (chain.matrixId && chain.status === 'Draft') {
        return updateEscalationMatrix(chain.matrixId, payload);
      }
      if (chain.matrixId && chain.status === 'Active') {
        return updateEscalationMatrix(chain.matrixId, payload);
      }
      const created = await createEscalationMatrix(payload);
      if (created.success) {
        return created;
      }
      if (/already exists/i.test(created.error || '')) {
        const existing = await getEscalationMatrixByBand(facilityId, chain.criticalityBand);
        if (existing.success && existing.data) {
          return updateEscalationMatrix(existing.data.id, payload);
        }
      }
      return created;
    };

    const applyApiValidationErrors = (
      band: CriticalityBand,
      apiErrors: MatrixValidationError[]
    ) => {
      setActivationErrors(prev => [
        ...prev,
        ...apiErrors.map(err => ({ ...err, criticalityBand: band })),
      ]);
      setFieldErrorsByBand(prev => ({
        ...prev,
        [band]: groupFieldErrorsByLevel(apiErrors),
      }));
    };

    const handleSave = async () => {
      const majorErrors = validateMatrixChain(chains.Major, contacts);
      const minorErrors = validateMatrixChain(chains.Minor, contacts);
      const activationErrors = validateForActivation(chains, contacts);

      setShowValidation(true);
      setValidationErrors({ Major: majorErrors, Minor: minorErrors });
      setFieldErrorsByBand({
        Major: groupFieldErrorsByLevel(majorErrors),
        Minor: groupFieldErrorsByLevel(minorErrors),
      });
      setActivationErrors(activationErrors);

      if (majorErrors.length || minorErrors.length || activationErrors.length) {
        uiNotificationService?.show({
          title: 'Validation failed',
          message: 'Complete all required fields before saving the matrix.',
          type: 'error',
          duration: 4000,
        });
        return;
      }

      setSaving(true);
      const saveResults = await Promise.all(
        CRITICALITY_BANDS.map(async bandMeta => ({
          band: bandMeta.value,
          result: await persistChain(chains[bandMeta.value]),
        }))
      );

      const saveFailed = saveResults.find(item => !item.result.success);
      if (saveFailed) {
        setSaving(false);
        if (saveFailed.result.validationErrors?.length) {
          applyApiValidationErrors(
            saveFailed.band,
            saveFailed.result.validationErrors.map(err => ({
              message: err.message,
              levelNumber: err.levelNumber,
              field: err.field,
              criticalityBand: err.criticalityBand,
            }))
          );
        }
        uiNotificationService?.show({
          title: 'Save failed',
          message: saveFailed.result.error || `Unable to save ${saveFailed.band} matrix`,
          type: 'error',
          duration: 5000,
        });
        return;
      }

      const activateResults = await Promise.all(
        saveResults.map(item => activateEscalationMatrix(item.result.data!.id, facilityId))
      );

      const activateFailedIndex = activateResults.findIndex(item => !item.success);
      if (activateFailedIndex >= 0) {
        const activateFailed = activateResults[activateFailedIndex];
        const band = CRITICALITY_BANDS[activateFailedIndex]?.value || 'Major';
        if (activateFailed.validationErrors?.length) {
          applyApiValidationErrors(
            band,
            activateFailed.validationErrors.map(err => ({
              message: err.message,
              levelNumber: err.levelNumber,
              field: err.field,
              criticalityBand: err.criticalityBand,
            }))
          );
        } else {
          setActivationErrors([
            { message: activateFailed.error || 'Save failed', criticalityBand: band },
          ]);
        }
        uiNotificationService?.show({
          title: 'Save failed',
          message: activateFailed.error || 'Unable to save escalation matrix',
          type: 'error',
          duration: 5000,
        });
        setSaving(false);
        await loadData();
        return;
      }

      setSaving(false);
      setShowValidation(false);
      setActivationErrors([]);
      uiNotificationService?.show({
        title: 'Escalation matrix saved. Applies to new critical events.',
        message: '',
        type: 'success',
        duration: 4000,
      });
      await loadData();
    };

    return (
      <section className="esc-step-card">
        <header className="esc-step-header">
          <p className="esc-step-label">STEP 2</p>
          <div className="esc-step-title-row">
            <h2 className="esc-step-title">Escalation matrix</h2>
          </div>
          <p className="esc-step-subtitle">
            Define the recipient chain and acknowledgement timers for each criticality band.
          </p>
        </header>

        <div className="esc-info-banner">
          <Info
            size={18}
            strokeWidth={ICON_STROKE_WIDTH}
            className="esc-info-icon"
          />
          <p>
            When a critical result isn&apos;t acknowledged within a level&apos;s timer, the alert
            advances to the next recipient in the chain. Changes apply to <strong>new</strong>{' '}
            critical events — active escalations keep the configuration they started with.
          </p>
        </div>

        {loadError && (
          <p
            className="esc-error-banner"
            role="alert"
          >
            {loadError}
          </p>
        )}

        <EscalationValidationSummary errors={activationErrors} />

        {loading ? (
          <p className="esc-placeholder-text">Loading escalation matrix…</p>
        ) : (
          <div className="esc-matrix-chains">
            {CRITICALITY_BANDS.map(bandMeta => (
              <EscalationChainCard
                key={bandMeta.value}
                chain={chains[bandMeta.value]}
                contacts={contacts}
                errors={validationErrors[bandMeta.value]}
                fieldErrorsByLevel={fieldErrorsByBand[bandMeta.value]}
                showValidation={showValidation}
                disabled={saving}
                onChange={chain => handleChainChange(bandMeta.value, chain)}
              />
            ))}
          </div>
        )}

        <div className="esc-matrix-save-row">
          <button
            type="button"
            className="esc-btn esc-btn--primary"
            disabled={loading || saving}
            onClick={handleSave}
          >
            <Save
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            {saving ? 'Saving…' : 'Save matrix'}
          </button>
        </div>

        <p className="ris-config-footer-link">
          Looking for live alerts? Open{' '}
          <Link to="/escalation-monitor">
            <b>RIS Analytics → Escalation monitor</b>
          </Link>{' '}
          to track active escalations and SLA status in real time.
        </p>
      </section>
    );
  }
);
